import { usePermission } from '@ctsu/loader';
import { parse, format, setHours, setMinutes, isValid } from 'date-fns';

export const safeTrim = (str) => {
  return typeof str === 'string' ? str.trim() : str;
};

export function mapDefaultValues(source, fieldConfig, fieldMapping = {}) {
  const defaults = {};

  Object.entries(fieldConfig).forEach(([field, defaultValue]) => {
    if (fieldMapping?.hasOwnProperty(field)) {
      defaults[field] = fieldMapping[field];
    } else {
      defaults[field] = source?.[field] ?? defaultValue;
    }
  });

  return defaults;
}


export const getOhrpCategoriesDefault = (requestType, hasCompletedRequest) => {
  if (requestType === 'NEW_SUBMISSION') {
    return [];
  }
  if (requestType === 'AMENDMENT') {
    return ["Allowed per LPO"];
  }
  if (requestType === 'CORRECTION') {
    if (hasCompletedRequest) {
      return ["Allowed per LPO"];
    }
    return [];
  }
  return [];
};


export const removeElementReconfirm = () => {
  const target = document.getElementById('ctsu-Alert-id');
  if (target) {
    target.parentNode.removeChild(target);
  }
};


export const getUserFriendlyError = (apiError, constraintMappings = {}) => {
  const defaultMessage = 'Unable to complete the request. Contact CTSU Help Desk, ctsucontact@westat.com.';
  if (apiError.error) {
    const foundConstraint = Object.keys(constraintMappings).find(constraint =>
      apiError.error.includes(constraint)
    );

    if (foundConstraint) {
      return constraintMappings[foundConstraint];
    }

    return defaultMessage;
  }

  return defaultMessage;
};


export function formatWithTime(dateStr) {
  const parsedDate = parse(dateStr, 'dd-MMM-yyyy', new Date());
  if (isValid(parsedDate)) {
    const midnightDate = setMinutes(setHours(parsedDate, 0), 0);
    return format(midnightDate, 'dd-MMM-yyyy hh:mm:ss a');
  }

  return dateStr;
}

// eslint-disable-next-line no-console
export const log = (data) => console.log("Logging...", data);


export const RequestStatus = {
  NEW: 'New',
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  IN_PROGRESS: 'In Progress',
  PENDING_SIGNOFF: 'Pending Sign-off',
  COMPLETED: 'Completed',
  UAT_SETUP: 'UAT Setup',
  GROUP_TESTING: 'Group Testing',
  CTSU_TESTING: 'CTSU Testing',
  CANCELED: 'Canceled',
  RETIRED: 'Retired',
  OPTIONAL_TRACKING: 'Completed',
  ADDITIONAL_SIGNOFF: 'In Progress',
  INACTIVE: 'Inactive'
}



export const STATUS_PROGRESSION = {
  'OPEN_RAVE': [
    'DRAFT',
    'SUBMITTED',
    'UAT_SETUP',
    'GROUP_TESTING',
    'CTSU_TESTING',
    'PENDING_SIGNOFF',
    'COMPLETED',
    'OPTIONAL_TRACKING',
    'CANCELED',
    'RETIRED'
  ],
  'REGULATORY': [
    'DRAFT',
    'SUBMITTED',
    'ADDITIONAL_SIGNOFF',
    'IN_PROGRESS',
    'COMPLETED',
    'CANCELED',
    'RETIRED'
  ]
};

export const getNextStatus = (currentStatus, requestType) => {
  const progression = STATUS_PROGRESSION[requestType];
  if (!progression) {
    return 'unknown';
  }

  const currentIndex = progression.indexOf(currentStatus);
  if (currentIndex === -1 || currentIndex === progression.length - 1) {
    return 'unknown';
  }

  return progression[currentIndex + 1];
};

export function getStatusPosition(currentStatus, targetStatus, requestType) {
  const progression = STATUS_PROGRESSION[requestType];
  if (!progression) {
    return 'unknown';
  }

  const currentIndex = progression.indexOf(currentStatus);
  const targetIndex = progression.indexOf(targetStatus);


  if (currentIndex === -1 || targetIndex === -1) {
    return 'unknown';
  }

  if (targetIndex < currentIndex) {
    return -1;
  }
  if (targetIndex === currentIndex) {
    return 0;
  }
  return 1;
}


export function compareObjects(original, current, path = '') {
  if (!original?.requestNumber) {
    return 'initial save';
  }
  const changes = [];

  Object.keys(current).forEach(key => {
    const currentPath = path ? `${path}.${key}` : key;
    const originalValue = original[key];
    const currentValue = current[key];
    if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
      changes.push(...compareObjects(originalValue || {}, currentValue, currentPath));
    } else if (originalValue !== currentValue) {
      changes.push(currentPath);
    }
  });
  return changes;
}


export const getCountryLabels = (countries, countryList) => {
  if (!Array.isArray(countries) || !Array.isArray(countryList)) {
    return [];
  }
  const countryMap = new Map(countryList.map(c => [c.id, c.value]));
  return countries?.map(country => countryMap.get(country.countryId) || null);
};

export const getSelectedCountryIds = (countries, upsertedCountries, countryList) => {
  return countries?.map((c) => {
    const tempCountry = countryList.find(x => x.value === c);
    return { psRcId: upsertedCountries.find(req => req.countryId === tempCountry?.id)?.psRcId || null, countryId: tempCountry?.value }
  });
}

export const parseBoolean = (value) => {
  if (value) {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    return false;
  }
  return value;
};

export const softValidateForm = (obj, fields) => {
  return fields.every(field => {
    const value = obj[field];
    if (value === null || value === undefined) return false;

    if (Array.isArray(value)) return value.length > 0;

    return true;
  })
}

export function dispatchRefreshEvent() {
  const refreshEvent = new CustomEvent("refresh", {
    bubbles: true,
    cancelable: true
  });
  document.dispatchEvent(refreshEvent);
}



export const withSeconds=(dateStr) => dateStr && dateStr.replace(/( AM| PM)/, ':00 $1');


export const useSetupPermission = per => {
  if (!per) return true;

  const permissions = Array.isArray(per) ? per : [per];

  const results = permissions.map(p => usePermission(p));

  return results.some(Boolean);
};

export const calculateUpdatedFields = (dirtyFields, currentData, defaultValues) => {
  const updatedFields = {};

  Object.keys(dirtyFields).forEach((field) => {
    if (JSON.stringify(currentData[field]) !== JSON.stringify(defaultValues[field])) {
      updatedFields[field] = currentData[field];
    }
  });

  return updatedFields;
};